import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useHandleSignInCallback } from '@logto/react';

import { env } from '@/env';
import { AUTH_INAPP, discardPkceState, exchangeInAppAuthCode } from '@/lib/logto';
import { consegnaAllOpener, eStatoFinestra, type Consegna } from '@/lib/loginFinestra';

/**
 * Two-mode callback:
 *
 *  1. **Standalone web**: standard Logto callback — `useHandleSignInCallback`
 *     parses the auth code, completes the PKCE exchange, persists tokens.
 *
 *  2. **External browser bridge** (Telegram flow): the system browser lands
 *     here after Logto. We don't run the SDK callback (its PKCE state lives
 *     in the WebView's localStorage, not the system browser's). Instead we
 *     redirect to a `t.me/<bot>/app?startapp=auth_<code>` deep link so the
 *     mini app reopens with the code accessible via `tgWebAppStartParam`.
 *
 * Detection: we treat any callback that opens *outside* a Telegram WebView as
 * a bridge invocation. `Telegram.WebApp.initData` is empty in the system
 * browser even when navigated from the WebView, so it's a reliable signal.
 */
export function AuthCallback() {
  const isInTelegram =
    !!(window as unknown as { Telegram?: { WebApp?: { initData?: string } } }).Telegram?.WebApp
      ?.initData;
  // Set by `startTelegramSignIn` on desktop, where the mini app redirects ITSELF
  // to Logto instead of going through the browser. We're back on our own origin
  // in the same webview, so the PKCE verifier is still in localStorage and we
  // can finish the exchange right here — no deep link, no relaunch.
  //
  // Checked BEFORE the Telegram detection, not alongside it: the marker records
  // what WE started, while `initData` only says where we happen to be running.
  // In browser dev (mockEnv fakes the launch params but not `window.Telegram`)
  // the two disagree, and trusting the environment there would send a perfectly
  // good in-app code down the deep-link path.
  const inAppFlow = localStorage.getItem(AUTH_INAPP) === '1';

  // La FINESTRA di Telegram Web (`lib/loginFinestra.ts`), SOLO col flag: la riconosce il prefisso dello `state`, che e' l'unica
  // cosa nostra che attraversa Logto (lo storage di questa finestra non e' quello dell'iframe che l'ha aperta). Prima degli altri
  // due: un `AUTH_INAPP` rimasto nello storage di primo livello non deve portare il codice della finestra a uno scambio QUI.
  // Il flag LETTERALE, qui dove decide (il bundler lo piega in questo modulo).
  if (import.meta.env.VITE_FEATURE_LOGIN_FINESTRA_ENABLED === 'true' && eStatoFinestra(new URLSearchParams(window.location.search).get('state'))) {
    return <FinestraCallback />;
  }
  if (inAppFlow) return <InAppCallback />;
  return isInTelegram ? <StandardCallback /> : <BridgeCallback />;
}

/**
 * Standalone web: the Logto SDK owns the PKCE state, so let it finish the
 * exchange. Only reachable with a real Telegram `initData` present (otherwise
 * `main.tsx` takes the bare fast path), or in a plain browser tab.
 */
function StandardCallback() {
  const { t } = useTranslation('general', { useSuspense: false });
  const navigate = useNavigate();
  const { isLoading, error } = useHandleSignInCallback(() => {
    navigate('/', { replace: true });
  });

  useEffect(() => {
    if (error) console.error('[auth] Logto sign-in callback failed:', error);
  }, [error]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-bg p-6 text-text">
      <p className="text-fg-muted">
        {isLoading ? t('auth.signingIn') : error ? t('auth.failed') : t('auth.redirecting')}
      </p>
      {error && (
        <button
          onClick={() => navigate('/', { replace: true })}
          className="rounded-md border border-border px-4 py-2 text-sm"
        >
          {t('auth.retry')}
        </button>
      )}
    </div>
  );
}

/**
 * Desktop / Telegram Web: completes OUR manual PKCE exchange in place.
 *
 * Not `useHandleSignInCallback`: that drives the Logto SDK's own PKCE state,
 * while this flow's verifier was generated by `buildAuthUrl` and lives under our
 * keys. Using the SDK here would look right and fail at the exchange.
 *
 * ⚠️ NO HOOKS THAT NEED A PROVIDER. `main.tsx` renders `<AuthCallback />` BARE
 * on `/auth/callback` when there is no Telegram `initData` — no Router, no
 * Redux, no LogtoProvider (see the fast path in `bootstrap`). The first version
 * of this component used `useNavigate` + `useAppDispatch` and crashed with
 * "useNavigate() may be used only in the context of a <Router> component" the
 * moment a real client landed here. So: a hard `location.replace('/')` instead
 * of router navigation, and no dispatch at all.
 *
 * The full reload is not a workaround, it is the better ending: the app boots
 * from scratch, `useLogtoAuth` finds the freshly stored tokens and opens the
 * gate through its normal path — no hand-dispatched state to keep in sync.
 */
function InAppCallback() {
  const { t } = useTranslation('general', { useSuspense: false });
  const [failed, setFailed] = useState(false);
  const code = useMemo(() => new URLSearchParams(window.location.search).get('code'), []);
  const error = useMemo(() => new URLSearchParams(window.location.search).get('error'), []);
  const state = useMemo(() => new URLSearchParams(window.location.search).get('state'), []);
  // Lo state si CONSUMA una volta sola, e in dev StrictMode monta l'effetto due volte: il secondo giro
  // lo troverebbe gia' cancellato e fallirebbe un login buono. La ref sopravvive al doppio giro e
  // tiene UNA sola promessa di scambio.
  const exchange = useRef<Promise<boolean> | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Cleared up front, on every outcome: a stale marker would send the NEXT
    // sign-in — possibly a mobile one on the same account — down this path.
    localStorage.removeItem(AUTH_INAPP);

    if (error || !code) {
      // Il flusso e' finito comunque: lo state salvato non deve sopravvivergli.
      discardPkceState();
      setFailed(true);
      return;
    }
    // Fail-closed: lo `state` di ritorno deve essere quello salvato PRIMA del code exchange.
    exchange.current ??= exchangeInAppAuthCode(code, state);
    void exchange.current.then((ok) => {
      if (cancelled) return;
      if (ok) window.location.replace('/');
      else setFailed(true);
    });
    return () => {
      cancelled = true;
    };
  }, [code, error, state]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-bg p-6 text-text">
      <h1 className="text-2xl font-bold text-brand">QLASH</h1>
      <p className="text-center text-sm text-fg-muted">
        {failed ? t('auth.failed') : t('auth.signingIn')}
      </p>
      {failed && (
        <button
          onClick={() => window.location.replace('/')}
          className="rounded-md border border-border px-4 py-2 text-sm"
        >
          {t('auth.retry')}
        </button>
      )}
    </div>
  );
}

/**
 * Telegram Web, nella finestra di primo livello: consegna `{code, state}` all'iframe che l'ha aperta e si chiude. NESSUNO scambio
 * qui (il verifier e lo state stanno nello storage dell'iframe), nessuno storage scritto, nessuna navigazione: senza opener, il
 * messaggio «torna alla mini app e riprova», e basta.
 *
 * ⚠️ Come `InAppCallback`, niente hook che vogliano un provider: `main.tsx` rende questo componente NUDO.
 */
function FinestraCallback() {
  const { t } = useTranslation('general', { useSuspense: false });
  const [consegna, setConsegna] = useState<Consegna | null>(null);
  // Una consegna sola, anche sotto StrictMode che monta l'effetto due volte.
  const fatta = useRef<Consegna | null>(null);
  useEffect(() => {
    fatta.current ??= consegnaAllOpener(window);
    setConsegna(fatta.current);
  }, []);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-bg p-6 text-text">
      <h1 className="text-2xl font-bold text-brand">QLASH</h1>
      <p className="text-center text-sm text-fg-muted" data-login-finestra={consegna ?? 'in-corso'}>
        {consegna === 'senza-opener'
          ? t('auth.finestra.senzaOpener')
          : consegna === 'consegnato'
            ? t('auth.finestra.consegnato')
            : t('auth.signingIn')}
      </p>
    </div>
  );
}

function BridgeCallback() {
  const { t } = useTranslation('general', { useSuspense: false });
  const code = useMemo(() => new URLSearchParams(window.location.search).get('code'), []);
  const error = useMemo(() => new URLSearchParams(window.location.search).get('error'), []);
  const [autoOpenFailed, setAutoOpenFailed] = useState(false);

  const deepLink = useMemo(() => {
    if (!code) return null;
    // Telegram Main Mini App link format: t.me/<bot>?startapp=<param>
    // (NOT t.me/<bot>/app — that path expects a named Direct Link app
    // registered via /newapp in BotFather, which we haven't created. The
    // wrong path silently opens the bot chat instead of relaunching the
    // mini app, dropping the start_param.)
    return `https://t.me/${env.telegramBotUsername}?startapp=auth_${encodeURIComponent(code)}`;
  }, [code]);

  useEffect(() => {
    if (!deepLink) return;
    // Try auto-redirect; if the browser blocks navigation away, surface a
    // manual button after ~1.5s so the user can tap it.
    window.location.href = deepLink;
    const t = window.setTimeout(() => setAutoOpenFailed(true), 1500);
    return () => window.clearTimeout(t);
  }, [deepLink]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-bg p-6 text-text">
      <h1 className="text-2xl font-bold text-brand">QLASH</h1>
      {error && (
        <p className="text-center text-sm text-danger">
          {t('auth.failedWithReason', { error })}
        </p>
      )}
      {!error && code && (
        <>
          <p className="text-center text-sm text-fg-muted">
            {t('auth.returning')}
          </p>
          {autoOpenFailed && deepLink && (
            <a
              href={deepLink}
              className="rounded-md bg-brand px-6 py-3 text-sm font-medium text-brand-fg"
            >
              {t('auth.openTelegram')}
            </a>
          )}
        </>
      )}
      {!error && !code && (
        <p className="text-center text-sm text-fg-muted">
          {t('auth.missingCode')}
        </p>
      )}
    </div>
  );
}
